import { z } from "zod";

import { formatMandateMessage, type MandateMessage, type MandateMessageValue } from "@/src/features/mandates/mandateMessage";
import { msg } from "@/src/shared/i18n/msg";

/**
 * Standing mandates (docs/security/injection-quarantine.md, "Standing
 * mandates"): a narrow kind of action the user approves once, which Smart
 * Manager then runs on its own with no tap. The first kind is `rain_move`:
 * when rain is forecast during one of the user's own outdoor plans, move it
 * to the dry free time the feed found and tell the people already on it.
 *
 * Safety is provenance, not a tap:
 * - The grant is the user's own act (a user-session route writes it under the
 *   user's write label; a grant any other code wrote is not honoured).
 * - The run carries intent only from the grant and the feed's own planner.
 *   The plan must be one the user put on the calendar (their own row, the
 *   organizer provably them), and the email reaches only people already on it.
 * - A rate cap, no spend, one automatic move per plan, an Undo on every run,
 *   and Off in Settings that stops the next run.
 *
 * Pure: no I/O, so the same rules hold in the feed, the job and the tests.
 */

export const STANDING_MANDATE_TYPE = "standing-mandate";
export const STANDING_MANDATE_RUNS_TYPE = "standing-mandate-runs";

export const STANDING_MANDATE_KINDS = ["rain_move"] as const;
export type StandingMandateKind = (typeof STANDING_MANDATE_KINDS)[number];

export function isStandingMandateKind(value: unknown): value is StandingMandateKind {
  return typeof value === "string" && (STANDING_MANDATE_KINDS as readonly string[]).includes(value);
}

/** What a rain-move grant allows. Fixed by code; a stored scope can only be this one. */
export const RAIN_MOVE_SCOPE = {
  ownPlansOnly: true,
  notify: "existing_attendees",
  maxRunsPer24h: 3,
  spendCents: 0,
} as const;

/**
 * Each kind's scope, fixed by code. A stored grant is honoured only when its
 * scope is exactly this one, so no stored value can widen what a kind may do.
 * Every scope carries its own `maxRunsPer24h`, the ledger's rate cap.
 */
export const STANDING_MANDATE_SCOPES = {
  rain_move: RAIN_MOVE_SCOPE,
} as const satisfies Record<StandingMandateKind, { readonly maxRunsPer24h: number }>;

export type StandingMandateScope = (typeof STANDING_MANDATE_SCOPES)[StandingMandateKind];

/** How long a run blocks another automatic run on the same item (unless the item has not ended yet). */
export const MANDATE_RUN_RETENTION_MS = 48 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * How long the ledger keeps a run at all: this week's and last week's digest
 * are rebuilt from it, so it covers two calendar weeks and a day of slack.
 */
export const MANDATE_LEDGER_KEEP_MS = 15 * DAY_MS;
/** Rate caps keep a kind under this: 3 a day for 15 days is 45. */
const MAX_LEDGER_RUNS = 50;

/**
 * How a stored grant was given. "settings" is read-only: the old Settings
 * toggle wrote it, and a grant it stored stays on (dropping it would turn
 * those rules off unseen). Nothing grants that way now; Settings only stops.
 */
export type MandateGrantVia = "card" | "voice" | "settings";
/** The ways a grant can be given now: a tap on the offer card, or the user's voice. */
export const MANDATE_GRANT_CHANGE_VIAS = ["card", "voice"] as const satisfies readonly MandateGrantVia[];
export type MandateGrantChangeVia = (typeof MANDATE_GRANT_CHANGE_VIAS)[number];

export type StandingMandateGrant = {
  kind: StandingMandateKind;
  status: "active" | "revoked";
  grantedAt: number;
  grantedVia: MandateGrantVia;
  revokedAt?: number;
  scope: StandingMandateScope;
};

const grantSchema = z.object({
  kind: z.enum(STANDING_MANDATE_KINDS),
  status: z.enum(["active", "revoked"]),
  grantedAt: z.number().int().positive(),
  grantedVia: z.enum(["card", "voice", "settings"]),
  revokedAt: z.number().int().positive().optional(),
  scope: z.record(z.string(), z.unknown()),
});

/** Whether a stored scope is exactly the kind's code scope: same keys, same values. */
function isKindScope(kind: StandingMandateKind, stored: Record<string, unknown>): boolean {
  const scope: Readonly<Record<string, unknown>> = STANDING_MANDATE_SCOPES[kind];
  const keys = Object.keys(scope);
  return Object.keys(stored).length === keys.length && keys.every((key) => Object.is(stored[key], scope[key]));
}

/**
 * A stored grant, or null when the value is not exactly a grant this code
 * writes: a known kind with exactly that kind's scope (and, when `kind` is
 * given, that kind).
 */
export function parseMandateGrant(value: unknown, kind?: StandingMandateKind): StandingMandateGrant | null {
  const parsed = grantSchema.safeParse(value);
  if (!parsed.success || (kind && parsed.data.kind !== kind) || !isKindScope(parsed.data.kind, parsed.data.scope)) return null;
  return { ...parsed.data, scope: STANDING_MANDATE_SCOPES[parsed.data.kind] };
}

export function standingMandateId(userId: string, kind: StandingMandateKind): string {
  return `${STANDING_MANDATE_TYPE}:${userId}:${kind}`;
}

export function standingMandateRunsId(userId: string, kind: StandingMandateKind): string {
  return `${STANDING_MANDATE_RUNS_TYPE}:${userId}:${kind}`;
}

/** The next grant after the user turns the mandate on or off. */
export type MandateGrantChange = { action: "grant"; via: MandateGrantChangeVia } | { action: "revoke" };

export function nextMandateGrant(
  kind: StandingMandateKind,
  current: StandingMandateGrant | null,
  change: MandateGrantChange,
  now: number,
): StandingMandateGrant | null {
  if (change.action === "grant") {
    if (current?.status === "active") return current;
    return { kind, status: "active", grantedAt: now, grantedVia: change.via, scope: STANDING_MANDATE_SCOPES[kind] };
  }
  if (!current || current.status === "revoked") return current;
  return { ...current, status: "revoked", revokedAt: now };
}

export type MandateRunStatus = "running" | "done" | "failed" | "undone" | "undo_failed";

export type MandateRunPerson = { email: string; name: string };

export type MandateRun = {
  runId: string;
  at: number;
  jobId: string;
  itemId: string;
  /** The title fixed copy may say (RainMove.title): the user's own, else "your outdoor plan". */
  title: string;
  /** Whether the copy may name the people (RainMove.peopleNamed); else they are counted. */
  peopleNamed?: boolean;
  timezone: string;
  from: { start: number; end: number };
  to: { start: number; end: number };
  /** The people the card would tell (already on the plan). */
  people: MandateRunPerson[];
  status: MandateRunStatus;
  /** Who the move email reached. Undo tells exactly these people. */
  sent?: string[];
  settledAt?: number;
  undoneAt?: number;
  /** Who the Undo's correction email reached. */
  undoSent?: string[];
};

const span = z.object({ start: z.number().finite(), end: z.number().finite() });
const runSchema = z.object({
  runId: z.string().min(1).max(200),
  at: z.number().int().positive(),
  jobId: z.string().max(256),
  itemId: z.string().min(1).max(512),
  title: z.string().max(400),
  peopleNamed: z.boolean().optional(),
  timezone: z.string().min(1).max(100),
  from: span,
  to: span,
  people: z.array(z.object({ email: z.string().max(320), name: z.string().max(200) })).max(50),
  status: z.enum(["running", "done", "failed", "undone", "undo_failed"]),
  sent: z.array(z.string().max(320)).max(50).optional(),
  settledAt: z.number().int().positive().optional(),
  undoneAt: z.number().int().positive().optional(),
  undoSent: z.array(z.string().max(320)).max(50).optional(),
});

/** The ledger's runs; a malformed entry is dropped, never guessed at. */
export function parseMandateRuns(value: unknown): MandateRun[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const parsed = runSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
}

const planNotOver = (run: MandateRun, now: number) => run.to.end > now || run.from.end > now;

/** Runs still worth keeping: the two weeks the digest reads, and any whose plan has not ended. */
export function pruneMandateRuns(runs: readonly MandateRun[], now: number): MandateRun[] {
  return runs
    .filter((run) => now - run.at < MANDATE_LEDGER_KEEP_MS || planNotOver(run, now))
    .slice(-MAX_LEDGER_RUNS);
}

export type MandateRunRefusal = "rate_capped" | "already_moved";

/**
 * Adds a run to the ledger, or says why the mandate may not run it:
 * - `rate_capped`: the grant's runs in the last 24 hours are used up.
 * - `already_moved`: the plan was moved by the mandate before (even if it
 *   was undone since). A plan moves on its own at most once; after that the
 *   user gets the card, so Undo is never followed by the same move again.
 */
export function claimMandateRun(
  kind: StandingMandateKind,
  runs: readonly MandateRun[],
  run: MandateRun,
  now: number,
): { ok: true; runs: MandateRun[] } | { ok: false; reason: MandateRunRefusal } {
  const kept = pruneMandateRuns(runs, now);
  const blocking = kept.filter((existing) => now - existing.at < MANDATE_RUN_RETENTION_MS || planNotOver(existing, now));
  if (blocking.some((existing) => existing.itemId === run.itemId && existing.status !== "failed")) {
    return { ok: false, reason: "already_moved" };
  }
  const recent = kept.filter((existing) => now - existing.at < DAY_MS).length;
  if (recent >= STANDING_MANDATE_SCOPES[kind].maxRunsPer24h) return { ok: false, reason: "rate_capped" };
  return { ok: true, runs: [...kept, run] };
}

/** The ledger with one run changed, or null when the run is not there. */
export function updateMandateRun(
  runs: readonly MandateRun[],
  runId: string,
  change: (run: MandateRun) => MandateRun,
): MandateRun[] | null {
  const index = runs.findIndex((run) => run.runId === runId);
  if (index < 0) return null;
  const next = [...runs];
  next[index] = change(runs[index]);
  return next;
}

/** The names of the people a run's email reached, in the order the plan lists them. */
export function mandateRunToldNames(run: Pick<MandateRun, "people" | "sent">): string[] {
  return toldNames(run.people, run.sent);
}

function toldNames(people: readonly MandateRunPerson[], emails: readonly string[] | undefined): string[] {
  const sent = new Set((emails ?? []).map((email) => email.trim().toLowerCase()));
  return people
    .filter((person) => sent.has(person.email.trim().toLowerCase()))
    .map((person) => person.name.trim() || person.email);
}

/** The title a rain move's copy says for a plan someone else wrote. */
export const OUTSIDE_PLAN_TITLE = msg("your outdoor plan");

/**
 * Every line a rain-move run can say, as catalog templates (registered in
 * `src/shared/i18n/mandateMessages.ts`). A done or undone run says who its
 * email reached: nobody, the names (at most 3, where the copy may name
 * them), or a count.
 */
export const RAIN_MOVE_RUN_TEMPLATES = {
  running: msg("Rain at {from}. Moving {title} to {to}…"),
  done: msg("Rain at {from}. I moved {title} to {to}."),
  doneToldNames: msg("Rain at {from}. I moved {title} to {to} and told {names}."),
  doneToldOne: msg("Rain at {from}. I moved {title} to {to} and told the person on it."),
  doneToldCount: msg("Rain at {from}. I moved {title} to {to} and told the {count} people on it."),
  failed: msg("Rain at {from}. I didn't move {title}: it changed after I checked. Nobody was emailed."),
  undone: msg("Moved {title} back to {from}."),
  undoneToldNames: msg("Moved {title} back to {from} and told {names}."),
  undoneToldOne: msg("Moved {title} back to {from} and told the person on it."),
  undoneToldCount: msg("Moved {title} back to {from} and told the {count} people on it."),
  undoFailed: msg("I couldn't move {title} back to {from}: it changed since. It's still at {to}."),
} as const;

/** The fixed copy a rain-move run's message may carry as a value. */
export const RAIN_MOVE_RUN_COPY: readonly string[] = [OUTSIDE_PLAN_TITLE];

type Told = { template: "Names"; names: string[] } | { template: "One" } | { template: "Count"; count: number } | null;

function told(run: MandateRun): Told {
  // An undone run reports its correction email, not the first one.
  const names = toldNames(run.people, run.status === "undone" ? run.undoSent : run.sent);
  if (names.length === 0) return null;
  if (run.peopleNamed !== true) return names.length === 1 ? { template: "One" } : { template: "Count", count: names.length };
  return names.length > 3 ? { template: "Count", count: names.length } : { template: "Names", names };
}

/**
 * What the run did, in one line: "Rain at 1 PM. I moved Walk with Maya to
 * 4 PM and told Maya." Fixed copy: the title and names are said only where
 * the row's label says the user wrote them; else "your outdoor plan" and
 * "the person on it". Kept as a template and values so each reader sees it
 * in their language, with times in their locale's format.
 */
export function mandateRunMessage(run: MandateRun): MandateMessage {
  const from = { time: run.from.start, timeZone: run.timezone };
  const to = { time: run.to.start, timeZone: run.timezone };
  const title: MandateMessageValue = run.title === OUTSIDE_PLAN_TITLE ? { copy: OUTSIDE_PLAN_TITLE } : run.title;
  const withTold = (prefix: "done" | "undone", values: Record<string, MandateMessageValue>): MandateMessage => {
    const reached = told(run);
    if (!reached) return { template: RAIN_MOVE_RUN_TEMPLATES[prefix], values };
    if (reached.template === "Names") return { template: RAIN_MOVE_RUN_TEMPLATES[`${prefix}ToldNames`], values: { ...values, names: { list: reached.names } } };
    if (reached.template === "Count") return { template: RAIN_MOVE_RUN_TEMPLATES[`${prefix}ToldCount`], values: { ...values, count: reached.count } };
    return { template: RAIN_MOVE_RUN_TEMPLATES[`${prefix}ToldOne`], values };
  };
  switch (run.status) {
    case "running":
      return { template: RAIN_MOVE_RUN_TEMPLATES.running, values: { from, title, to } };
    case "done":
      return withTold("done", { from, title, to });
    case "failed":
      return { template: RAIN_MOVE_RUN_TEMPLATES.failed, values: { from, title } };
    case "undone":
      return withTold("undone", { title, from });
    case "undo_failed":
      return { template: RAIN_MOVE_RUN_TEMPLATES.undoFailed, values: { title, from, to } };
  }
}

/** The run's line in source English, as the server stores and the assistant says it. */
export function mandateRunSummary(run: MandateRun): string {
  return formatMandateMessage(mandateRunMessage(run));
}

/** Until when Undo can put a run's plan back: before the new time starts, and only once it is done. */
export function mandateRunUndoUntil(run: Pick<MandateRun, "status" | "to">): number | null {
  return run.status === "done" ? run.to.start : null;
}
